import React, { useEffect, useState, useCallback } from 'react';
import Layout from './Layout';
import './css/MasterOpening.css';

const MasterOpening = () => {
  const [user, setUser] = useState(null);
  
  // Three-level structure states
  const [groups, setGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [openingData, setOpeningData] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dragError, setDragError] = useState(''); 
  
  // Expanded states for three-level accordion
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  
  // Selection states
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  
  // Drag and drop states
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedCategoryId, setDraggedCategoryId] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSequenceUpdating, setIsSequenceUpdating] = useState(false);
  const [preventFormSubmission, setPreventFormSubmission] = useState(false);

  // Modal states
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showTextModal, setShowTextModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Edit states
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [deletingGroup, setDeletingGroup] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  
  // Form states
  const [groupForm, setGroupForm] = useState({
    title: '',
    description: ''
  });
  
  const [editGroupForm, setEditGroupForm] = useState({
    title: '',
    description: '',
    status: 1
  });
  
  const [categoryForm, setCategoryForm] = useState({
    title: '',
    keywords: '',
    groupId: null
  });
  
  const [editCategoryForm, setEditCategoryForm] = useState({
    title: '',
    keywords: '',
    groupId: null
  });
  
  const [editForm, setEditForm] = useState({
    title: '',
    textContent: '',
    mediaName: '',
    status: 1,
    categoryId: null,
    image: null,
    video: null,
    audio: null
  });
  
  const [textForm, setTextForm] = useState({
    title: '',
    textContent: '',
    categoryId: null
  });
  
  const [imageForm, setImageForm] = useState({
    title: '',
    categoryId: null,
    image: null
  });
  
  const [videoForm, setVideoForm] = useState({
    title: '',
    categoryId: null,
    video: null
  });

  const [audioForm, setAudioForm] = useState({
    title: '',
    categoryId: null,
    audio: null
  });

  // Preview states
  const [imagePreview, setImagePreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [audioPreview, setAudioPreview] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  const [editVideoPreview, setEditVideoPreview] = useState(null);
  const [editAudioPreview, setEditAudioPreview] = useState(null);
  const [videoLoadingStates, setVideoLoadingStates] = useState({});
  
  // Helper functions
  const getTypeLabel = (type) => {
    switch(type) {
      case 1: return 'Text';
      case 2: return 'Image';
      case 3: return 'Video';
      case 4: return 'Audio';
      default: return 'Unknown';
    }
  };
  
  const getGitHubUrl = (filename) => {
    if (!filename) return null;
    return `/api/masterOpening/media/${filename}`;
  };

  const getCurrentMediaUrl = (item) => {
    if (item.TYPE === 2 || item.TYPE === 3 || item.TYPE === 4) {
      if (item.MEDIANAME) {
        const githubUrl = getGitHubUrl(item.MEDIANAME);
        console.log('Generated GitHub URL:', githubUrl);
        return githubUrl;
      }
    }
    return null;
  };

  const handleMediaError = async (e, filename) => {
    console.error('Media load error for file:', filename);
    
    const isVideo = e.target.tagName === 'VIDEO';
    const isImage = e.target.tagName === 'IMG';
    
    e.target.style.display = 'none';
    
    if (e.target.parentNode.querySelector('.media-error-container')) {
      return;
    }
    
    const errorDiv = document.createElement('div');
    errorDiv.className = 'media-error-container';
    errorDiv.style.cssText = `
      padding: 15px; 
      text-align: center; 
      color: #666; 
      border: 2px dashed #dc3545; 
      border-radius: 8px; 
      background: linear-gradient(135deg, #fff5f5, #fed7d7);
      margin: 10px 0;
      font-family: Arial, sans-serif;
      min-height: ${isVideo ? '150px' : '100px'};
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    `;
    
    try {
      const response = await fetch(`/api/masterOpening/media/${filename}`);
      
      if (response.ok) {
        setTimeout(() => {
          e.target.style.display = 'block';
          e.target.src = `/api/masterOpening/media/${filename}?t=${Date.now()}`;
          
          if (isVideo) {
            e.target.load();
          }
        }, 1000);
        
        errorDiv.innerHTML = `
          <div style="font-size: 24px; margin-bottom: 8px; color: #007bff;">🔄</div>
          <div style="font-weight: bold; margin-bottom: 6px; color: #155724;">Retrying...</div>
          <div style="font-size: 12px; color: #975a16; margin-bottom: 8px;">${filename}</div>
        `;
        
        setTimeout(() => {
          if (errorDiv.parentNode) {
            errorDiv.remove();
          }
        }, 3000);
        
      } else {
        errorDiv.innerHTML = `
          <div style="font-size: 24px; margin-bottom: 8px; color: #dc3545;">
            ${isVideo ? '🎥' : '⚠️'}
          </div>
          <div style="font-weight: bold; margin-bottom: 6px; color: #721c24;">
            ${isVideo ? 'Video' : 'Image'} Loading Failed
          </div>
          <div style="font-size: 12px; color: #975a16; margin-bottom: 8px;">${filename}</div>
        `;
      }
    } catch (fetchError) {
      errorDiv.innerHTML = `
        <div style="font-size: 24px; margin-bottom: 8px; color: #dc3545;">🔌</div>
        <div style="font-weight: bold; margin-bottom: 6px; color: #721c24;">Connection Error</div>
        <div style="font-size: 12px; color: #975a16; margin-bottom: 8px;">${filename}</div>
      `;
    }
    
    e.target.parentNode.appendChild(errorDiv);
  };
  
  // Load functions
  const loadGroups = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch('/api/masterOpening/groups');
      const data = await response.json();

      if (data.success) {
        setGroups(Array.isArray(data.data) ? data.data : []);
      } else {
        setError(data.message || 'Failed to load groups');
        setGroups([]);
      }
    } catch (err) {
      setError('Error loading groups: ' + err.message);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };
  
  const loadCategories = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch('/api/masterOpening/categories');
      const data = await response.json();

      if (data.success) {
        setCategories(Array.isArray(data.data) ? data.data : []);
      } else {
        setError(data.message || 'Failed to load categories');
        setCategories([]);
      }
    } catch (err) {
      setError('Error loading categories: ' + err.message);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const loadOpeningData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch('/api/masterOpening/');
      const data = await response.json();

      if (data.success) {
        setOpeningData(Array.isArray(data.data) ? data.data : []);
      } else {
        setError(data.message || 'Failed to load opening data');
        setOpeningData([]);
      }
    } catch (err) {
      setError('Error loading opening data: ' + err.message);
      setOpeningData([]);
    } finally {
      setLoading(false);
    }
  };

  // Initialize data on component mount
  useEffect(() => {
    const userData = sessionStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
    }
    
    loadGroups();
    loadCategories();
    loadOpeningData();
  }, []);
  
  // Toggle functions for accordion
  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };
  
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };
  
  // Get categories by group
  const getCategoriesByGroup = (groupId) => {
    return categories.filter(cat => cat.GROUP_ID === groupId);
  };
  
  // Get items by category
  const getItemsByCategory = (categoryId) => {
    return openingData.filter(item => item.CATEGORY_ID === categoryId);
  };
  
  // Group handlers
  const handleAddGroup = () => {
    setGroupForm({ title: '', description: '' });
    setShowGroupModal(true);
    setError('');
    setSuccess('');
  };
  
  const handleGroupInputChange = (e) => {
    const { name, value } = e.target;
    setGroupForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleGroupSubmit = async (e) => {
    e.preventDefault();
    
    if (!groupForm.title.trim()) {
      setError('Group title is required');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch('/api/masterOpening/groups', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: groupForm.title.trim(),
          description: groupForm.description.trim()
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Group added successfully');
        loadGroups();
        setTimeout(() => {
          setShowGroupModal(false);
          setGroupForm({ title: '', description: '' });
          setSuccess('');
        }, 700);
      } else {
        setError(data.message || 'Failed to add group');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setEditGroupForm({
      title: group.TITLE,
      description: group.DESCRIPTION || '',
      status: group.STATUS
    });
    setShowEditGroupModal(true);
    setError('');
    setSuccess('');
  };
  
  const handleEditGroupInputChange = (e) => {
    const { name, value } = e.target;
    setEditGroupForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleEditGroupSubmit = async (e) => {
    e.preventDefault();
    
    if (!editingGroup) return;
    
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`/api/masterOpening/groups/${editingGroup.PKKEY}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: editGroupForm.title.trim(),
          description: editGroupForm.description.trim(),
          status: editGroupForm.status
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Group updated successfully');
        loadGroups();
        setTimeout(() => {
          setShowEditGroupModal(false);
          setEditingGroup(null);
          setSuccess('');
        }, 700);
      } else {
        setError(data.message || 'Failed to update group');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteGroup = (group) => {
    setDeletingGroup(group);
    setShowDeleteGroupModal(true);
  };
  
  const confirmDeleteGroup = async () => {
    if (!deletingGroup) return;
    
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`/api/masterOpening/groups/${deletingGroup.PKKEY}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Group deleted successfully');
        loadGroups();
        loadCategories();
        setTimeout(() => {
          setShowDeleteGroupModal(false);
          setDeletingGroup(null);
          setSuccess('');
        }, 700);
      } else {
        setError(data.message || 'Failed to delete group');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Category handlers
  const handleAddCategory = (groupId) => {
    setCategoryForm({ 
      title: '', 
      keywords: '',
      groupId: groupId
    });
    setSelectedGroupId(groupId);
    setShowCategoryModal(true);
    setError('');
    setSuccess('');
  };
  
  const handleCategoryInputChange = (e) => {
    const { name, value } = e.target;
    setCategoryForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    
    if (!categoryForm.title.trim() || !categoryForm.keywords.trim() || !categoryForm.groupId) {
      setError('All fields are required');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch('/api/masterOpening/categories', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: categoryForm.title.trim(),
          keywords: categoryForm.keywords.trim(),
          groupId: categoryForm.groupId
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Category added successfully');
        loadCategories();
        setTimeout(() => {
          setShowCategoryModal(false);
          setCategoryForm({ title: '', keywords: '', groupId: null });
          setSuccess('');
        }, 700);
      } else {
        setError(data.message || 'Failed to add category');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setEditCategoryForm({
      title: category.TITLE,
      keywords: category.KEYWORDS,
      groupId: category.GROUP_ID
    });
    setShowEditCategoryModal(true);
    setError('');
    setSuccess('');
  };
  
  const handleEditCategoryInputChange = (e) => {
    const { name, value } = e.target;
    setEditCategoryForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleEditCategorySubmit = async (e) => {
    e.preventDefault();
    
    if (!editingCategory) return;
    
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`/api/masterOpening/categories/${editingCategory.PKKEY}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: editCategoryForm.title.trim(),
          keywords: editCategoryForm.keywords.trim(),
          groupId: editCategoryForm.groupId
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Category updated successfully');
        loadCategories();
        setTimeout(() => {
          setShowEditCategoryModal(false);
          setEditingCategory(null);
          setSuccess('');
        }, 700);
      } else {
        setError(data.message || 'Failed to update category');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteCategory = (category) => {
    setDeletingCategory(category);
    setShowDeleteCategoryModal(true);
  };
  
  const confirmDeleteCategory = async () => {
    if (!deletingCategory) return;
    
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`/api/masterOpening/categories/${deletingCategory.PKKEY}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Category deleted successfully');
        loadCategories();
        loadOpeningData();
        setTimeout(() => {
          setShowDeleteCategoryModal(false);
          setDeletingCategory(null);
          setSuccess('');
        }, 700);
      } else {
        setError(data.message || 'Failed to delete category');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Item handlers (text, image, video)
  const handleAddText = (categoryId) => {
    setTextForm({ title: '', textContent: '', categoryId: categoryId });
    setSelectedCategoryId(categoryId);
    setShowTextModal(true);
    setError('');
    setSuccess('');
  };
  
  const handleAddImage = (categoryId) => {
    setImageForm({ title: '', categoryId: categoryId, image: null });
    setSelectedCategoryId(categoryId);
    setImagePreview(null);
    setShowImageModal(true);
    setError('');
    setSuccess('');
  };
  
  const handleAddVideo = (categoryId) => {
    setVideoForm({ title: '', categoryId: categoryId, video: null });
    setSelectedCategoryId(categoryId);
    setVideoPreview(null);
    setShowVideoModal(true);
    setError('');
    setSuccess('');
  };

  const handleAddAudio = (categoryId) => {
    setAudioForm({ title: '', categoryId: categoryId, audio: null });
    setSelectedCategoryId(categoryId);
    setAudioPreview(null);
    setShowAudioModal(true);
    setError('');
    setSuccess('');
  };
  
  const handleTextInputChange = (e) => {
    const { name, value } = e.target;
    setTextForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleImageInputChange = (e) => {
    const { name, value, files } = e.target;
    
    if (name === 'image' && files && files[0]) {
      setImageForm(prev => ({ ...prev, image: files[0] }));
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(files[0]);
    } else {
      setImageForm(prev => ({ ...prev, [name]: value }));
    }
  };
  
  const handleVideoInputChange = (e) => {
    const { name, value, files } = e.target;
    
    if (name === 'video' && files && files[0]) {
      setVideoForm(prev => ({ ...prev, video: files[0] }));
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setVideoPreview(reader.result);
      };
      reader.readAsDataURL(files[0]);
    } else {
      setVideoForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAudioInputChange = (e) => {
    const { name, value, files } = e.target;
    
    if (name === 'audio' && files && files[0]) {
      setAudioForm(prev => ({ ...prev, audio: files[0] }));
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setAudioPreview(reader.result);
      };
      reader.readAsDataURL(files[0]);
    } else {
      setAudioForm(prev => ({ ...prev, [name]: value }));
    }
  };
  
  const handleTextSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch('/api/masterOpening/text', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: textForm.title.trim(),
          textContent: textForm.textContent.trim(),
          categoryId: textForm.categoryId
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Text entry added successfully');
        loadOpeningData();
        setTimeout(() => {
          setShowTextModal(false);
          setTextForm({ title: '', textContent: '', categoryId: null });
          setSuccess('');
        }, 700);
      } else {
        setError(data.message || 'Failed to add text entry');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleImageSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      
      const formData = new FormData();
      formData.append('title', imageForm.title.trim());
      formData.append('categoryId', imageForm.categoryId);
      formData.append('image', imageForm.image);
      
      const response = await fetch('/api/masterOpening/image', {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Image entry added successfully');
        loadOpeningData();
        setTimeout(() => {
          setShowImageModal(false);
          setImageForm({ title: '', categoryId: null, image: null });
          setImagePreview(null);
          setSuccess('');
        }, 700);
      } else {
        setError(data.message || 'Failed to add image entry');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleVideoSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      
      const formData = new FormData();
      formData.append('title', videoForm.title.trim());
      formData.append('categoryId', videoForm.categoryId);
      formData.append('video', videoForm.video);
      
      const response = await fetch('/api/masterOpening/video', {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Video entry added successfully');
        loadOpeningData();
        setTimeout(() => {
          setShowVideoModal(false);
          setVideoForm({ title: '', categoryId: null, video: null });
          setVideoPreview(null);
          setSuccess('');
        }, 700);
      } else {
        setError(data.message || 'Failed to add video entry');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleAudioSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      
      const formData = new FormData();
      formData.append('title', audioForm.title.trim());
      formData.append('categoryId', audioForm.categoryId);
      formData.append('audio', audioForm.audio);
      
      const response = await fetch('/api/masterOpening/audio', {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Audio entry added successfully');
        loadOpeningData();
        setTimeout(() => {
          setShowAudioModal(false);
          setAudioForm({ title: '', categoryId: null, audio: null });
          setAudioPreview(null);
          setSuccess('');
        }, 700);
      } else {
        setError(data.message || 'Failed to add audio entry');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setEditForm({
      title: item.TITLE,
      textContent: item.TEXT_CONTENT  || '',
      mediaName: item.MEDIANAME || '',
      status: item.STATUS,
      categoryId: item.CATEGORY_ID,
      image: null,
      video: null,
      audio: null
    });
    
    if (item.TYPE === 2) {
      setEditImagePreview(getCurrentMediaUrl(item));
    } else if (item.TYPE === 3) {
      setEditVideoPreview(getCurrentMediaUrl(item));
    } else if (item.TYPE === 4) {
      setEditAudioPreview(getCurrentMediaUrl(item));
    }
    
    setShowEditModal(true);
    setError('');
    setSuccess('');
  };
  
  const handleEditInputChange = (e) => {
    const { name, value, files } = e.target;
    
    if (name === 'image' && files && files[0]) {
      setEditForm(prev => ({ ...prev, image: files[0] }));
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditImagePreview(reader.result);
      };
      reader.readAsDataURL(files[0]);
    } else if (name === 'video' && files && files[0]) {
      setEditForm(prev => ({ ...prev, video: files[0] }));
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditVideoPreview(reader.result);
      };
      reader.readAsDataURL(files[0]);
    } else if (name === 'audio' && files && files[0]) {
      setEditForm(prev => ({ ...prev, audio: files[0] }));
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditAudioPreview(reader.result);
      };
      reader.readAsDataURL(files[0]);
    } else {
      setEditForm(prev => ({ ...prev, [name]: value }));
    }
  };
  
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!editingItem) return;
    
    try {
      setLoading(true);
      setError('');
      
      const formData = new FormData();
      formData.append('title', editForm.title.trim());
      formData.append('categoryId', editForm.categoryId);
      formData.append('status', editForm.status);
      
      if (editingItem.TYPE === 1) {
        formData.append('textContent', editForm.textContent.trim());
      } else if (editingItem.TYPE === 2 && editForm.image) {
        formData.append('image', editForm.image);
      } else if (editingItem.TYPE === 3 && editForm.video) {
        formData.append('video', editForm.video);
      } else if (editingItem.TYPE === 4 && editForm.audio) {
        formData.append('audio', editForm.audio);
      }
      
      const response = await fetch(`/api/masterOpening/${editingItem.PKKEY}`, {
        method: 'PUT',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Entry updated successfully');
        loadOpeningData();
        setTimeout(() => {
          setShowEditModal(false);
          setEditingItem(null);
          setEditImagePreview(null);
          setEditVideoPreview(null);
          setEditAudioPreview(null);
          setSuccess('');
        }, 700);
      } else {
        setError(data.message || 'Failed to update entry');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = (item) => {
    setDeletingItem(item);
    setShowDeleteModal(true);
  };
  
  const confirmDelete = async () => {
    if (!deletingItem) return;
    
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`/api/masterOpening/${deletingItem.PKKEY}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Entry deleted successfully');
        loadOpeningData();
        setTimeout(() => {
          setShowDeleteModal(false);
          setDeletingItem(null);
          setSuccess('');
        }, 700);
      } else {
        setError(data.message || 'Failed to delete entry');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const closeModals = () => {
    setShowGroupModal(false);
    setShowCategoryModal(false);
    setShowTextModal(false);
    setShowImageModal(false);
    setShowVideoModal(false);
    setShowAudioModal(false);
    setShowEditGroupModal(false);
    setShowEditCategoryModal(false);
    setShowEditModal(false);
    setShowDeleteGroupModal(false);
    setShowDeleteCategoryModal(false);
    setShowDeleteModal(false);
    setError('');
    setSuccess('');
  };
  
  const renderVideoWithLoading = (videoUrl, videoId, styles = {}) => {
    const isLoading = videoLoadingStates[videoId] !== false;
    
    return (
      <div 
        className="video-wrapper" 
        data-loading={isLoading.toString()}
        data-state={isLoading ? 'loading' : 'loaded'}
        style={styles}
      >
        {isLoading && (
          <div className="video-loading-overlay">
            <span>Loading video...</span>
          </div>
        )}
        <video 
          key={videoId}
          controls 
          preload="metadata"
          onLoadedData={() => {
            setVideoLoadingStates(prev => ({ ...prev, [videoId]: false }));
          }}
          onError={(e) => {
            setVideoLoadingStates(prev => ({ ...prev, [videoId]: false }));
          }}
          style={{ 
            width: '100%',
            maxWidth: styles.maxWidth || '400px',
            maxHeight: styles.maxHeight || '300px'
          }}
        >
          <source src={videoUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>
    );
  };
  
  // Drag and drop handlers for items within categories
  const handleDragStart = (e, categoryId, itemIndex, item) => {
    console.log('=== DRAG START ===', { categoryId, itemIndex, item: item.PKKEY });
    setDraggedItem(itemIndex);
    setDraggedCategoryId(categoryId);
    setIsDragging(true);
    setPreventFormSubmission(true);
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
    
    e.dataTransfer.setData('text/plain', JSON.stringify({
      categoryId: categoryId,
      itemIndex: itemIndex,
      itemId: item.PKKEY
    }));
    
    setError('');
    setDragError('');
  };

  const handleDragEnd = (e) => {
    console.log('=== DRAG END ===');
    e.target.style.opacity = '1';
    setDraggedItem(null);
    setDraggedCategoryId(null);
    setDragOverIndex(null);
    setIsDragging(false);
    
    setTimeout(() => {
      setPreventFormSubmission(false);
    }, 300);
  };

  const handleDragOver = (e, categoryId, targetIndex) => {
    e.preventDefault();
    
    if (draggedCategoryId !== categoryId) {
      setDragError('Cannot drag items between different categories');
      return;
    }
    
    setDragError('');
    
    if (draggedItem !== null && draggedItem !== targetIndex) {
      setDragOverIndex(targetIndex);
    }
  };

  const handleDrop = async (e, categoryId, dropIndex) => {
    e.preventDefault();
    
    console.log('=== DROP TRIGGERED ===', { 
      categoryId, 
      dropIndex, 
      draggedItem, 
      draggedCategoryId 
    });
    
    if (draggedCategoryId !== categoryId) {
      setDragError('Cannot move items between categories');
      setDraggedItem(null);
      setDraggedCategoryId(null);
      setDragOverIndex(null);
      setIsDragging(false);
      return;
    }
    
    if (draggedItem === null || draggedItem === dropIndex) {
      console.log('Same position or null, skipping update');
      setDraggedItem(null);
      setDraggedCategoryId(null);
      setDragOverIndex(null);
      setIsDragging(false);
      return;
    }
    
    try {
      setIsSequenceUpdating(true);
      setError('');
      setDragError('');
      
      const categoryItems = getItemsByCategory(categoryId);
      const newItems = [...categoryItems];
      const [removed] = newItems.splice(draggedItem, 1);
      newItems.splice(dropIndex, 0, removed);
      
      const sequences = newItems.map((item, index) => ({
        id: item.PKKEY,
        sequence: index + 1
      }));
      
      console.log('Sending sequence update:', sequences);
      
      const response = await fetch('/api/masterOpening/sequence/update', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ sequences })
      });
      
      const data = await response.json();
      
      if (data.success) {
        console.log('Sequence updated successfully');
        await loadOpeningData();
        setSuccess('Sequence updated successfully');
        setTimeout(() => setSuccess(''), 2000);
      } else {
        setError(data.message || 'Failed to update sequence');
        await loadOpeningData();
      }
    } catch (err) {
      console.error('Error updating sequence:', err);
      setError('Error: ' + err.message);
      await loadOpeningData();
    } finally {
      setDraggedItem(null);
      setDraggedCategoryId(null);
      setDragOverIndex(null);
      setIsDragging(false);
      setIsSequenceUpdating(false);
    }
  };

  return (
    <Layout user={user}>
      <div className="homepage-container">
        <div className="homepage-header">
          <h1 className="homepage-title">Master Opening Management (3-Level)</h1>
        </div>
        
        {/* Filter Section */}
        <div className="filter-section">
          <div className="filter-controls">
            <p>Three-Level Hierarchical Structure: Groups → Categories → Items</p>
          </div>
          <div className="action-buttons">
            <button
              className="new-button category-button"
              onClick={handleAddGroup}
              disabled={loading}
            >
              + Add Group
            </button>
          </div>
        </div>
        
        {/* Global Messages */}
        {error && (
          <div style={{ 
            padding: '15px', 
            marginBottom: '20px', 
            backgroundColor: '#f8d7da', 
            color: '#721c24', 
            borderRadius: '4px',
            border: '1px solid #f5c6cb'
          }}>
            {error}
          </div>
        )}
        
        {success && (
          <div style={{ 
            padding: '15px', 
            marginBottom: '20px', 
            backgroundColor: '#d4edda', 
            color: '#155724', 
            borderRadius: '4px',
            border: '1px solid #c3e6cb'
          }}>
            {success}
          </div>
        )}
        
        {dragError && (
          <div style={{ 
            padding: '15px', 
            marginBottom: '20px', 
            backgroundColor: '#fff3cd', 
            color: '#856404', 
            borderRadius: '4px',
            border: '1px solid #ffeaa7'
          }}>
            {dragError}
          </div>
        )}
        
        {isSequenceUpdating && (
          <div style={{ 
            padding: '15px', 
            marginBottom: '20px', 
            backgroundColor: '#d1ecf1', 
            color: '#0c5460', 
            borderRadius: '4px',
            border: '1px solid #bee5eb'
          }}>
            Updating sequence...
          </div>
        )}
        
        {/* Groups Container (1st Level) */}
        <div className="groups-container">
          {loading && groups.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Loading...
            </div>
          ) : groups.length === 0 ? (
            <div className="no-categories-message">
              No groups found. Click "+ Add Group" to create your first group.
            </div>
          ) : (
            groups.map(group => {
              const isGroupExpanded = expandedGroups.has(group.PKKEY);
              const groupCategories = getCategoriesByGroup(group.PKKEY);
              
              return (
                <div key={group.PKKEY} className="category-section group-section">
                  {/* Group Header (1st Level) */}
                  <div 
                    className="category-header group-header" 
                    onClick={() => toggleGroup(group.PKKEY)}
                    style={{ backgroundColor: '#000A30' }}
                  >
                    <div className="category-info">
                      <h3 className="category-title">
                        <span className="expand-icon" style={{ 
                          transform: isGroupExpanded ? 'rotate(90deg)' : 'rotate(0deg)' 
                        }}>
                          ▶
                        </span>
                        {group.TITLE}
                        <span style={{ 
                          marginLeft: '15px', 
                          fontSize: '14px', 
                          opacity: 0.8,
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          padding: '4px 12px',
                          borderRadius: '12px'
                        }}>
                          {groupCategories.length} {groupCategories.length === 1 ? 'Category' : 'Categories'}
                        </span>
                      </h3>
                      {group.DESCRIPTION && (
                        <p className="category-keywords">{group.DESCRIPTION}</p>
                      )}
                      <span className={`category-status ${group.STATUS === 1 ? 'status-active' : 'status-inactive'}`}>
                        {group.STATUS === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="category-actions" onClick={(e) => e.stopPropagation()}>
					  {group.DEFAULT !== 1 && (
						<button 
							className="new-button" 
							onClick={() => handleAddCategory(group.PKKEY)}
							style={{ backgroundColor: '#28a745', marginRight: '10px' }}
						>
							+ Add Category
						</button>
					  )}
					  {group.DEFAULT !== 1 && (
						<button 
							className="edit-button" 
							onClick={() => handleEditGroup(group)}
						>
							Edit
						</button>
					  )}
                      {group.DEFAULT !== 1 && (
						<button 
							className="delete-button" 
							onClick={() => handleDeleteGroup(group)}
						>
							Delete
						</button>
					  )}
                    </div>
                  </div>
                  
                  {/* Categories Container (2nd Level) */}
                  {isGroupExpanded && (
                    <div className="category-content" style={{ padding: '20px', backgroundColor: '#f5f5f5' }}>
                      {groupCategories.length === 0 ? (
                        <div className="no-items-message">
                          No categories in this group. Click "+ Add Category" to create one.
                        </div>
                      ) : (
                        groupCategories.map(category => {
                          const isCategoryExpanded = expandedCategories.has(category.PKKEY);
                          const categoryItems = getItemsByCategory(category.PKKEY);
                          
                          return (
                            <div key={category.PKKEY} className="category-section" style={{ marginBottom: '15px' }}>
                              {/* Category Header (2nd Level) */}
                              <div 
                                className="category-header" 
                                onClick={() => toggleCategory(category.PKKEY)}
                                style={{ backgroundColor: '#003366' }}
                              >
                                <div className="category-info">
                                  <h4 className="category-title" style={{ fontSize: '18px' }}>
                                    <span className="expand-icon" style={{ 
                                      transform: isCategoryExpanded ? 'rotate(90deg)' : 'rotate(0deg)' 
                                    }}>
                                      ▶
                                    </span>
                                    {category.TITLE}
                                    <span style={{ 
                                      marginLeft: '15px', 
                                      fontSize: '13px', 
                                      opacity: 0.8,
                                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                      padding: '3px 10px',
                                      borderRadius: '12px'
                                    }}>
                                      {categoryItems.length} {categoryItems.length === 1 ? 'Item' : 'Items'}
                                    </span>
                                  </h4>
                                  <p className="category-keywords" style={{ fontSize: '13px' }}>
                                    Keywords: {category.KEYWORDS}
                                  </p>
                                </div>
                                <div className="category-actions" onClick={(e) => e.stopPropagation()}>
                                  <button 
                                    className="new-button" 
                                    onClick={() => handleAddText(category.PKKEY)}
                                    style={{ backgroundColor: '#007bff', fontSize: '13px', padding: '6px 12px' }}
                                  >
                                    + Text
                                  </button>
                                  <button 
                                    className="new-button" 
                                    onClick={() => handleAddImage(category.PKKEY)}
                                    style={{ backgroundColor: '#17a2b8', fontSize: '13px', padding: '6px 12px' }}
                                  >
                                    + Image
                                  </button>
                                  <button 
                                    className="new-button" 
                                    onClick={() => handleAddVideo(category.PKKEY)}
                                    style={{ backgroundColor: '#6f42c1', fontSize: '13px', padding: '6px 12px' }}
                                  >
                                    + Video
                                  </button>
                                  <button 
                                    className="new-button" 
                                    onClick={() => handleAddAudio(category.PKKEY)}
                                    style={{ backgroundColor: '#fd7e14', fontSize: '13px', padding: '6px 12px' }}
                                  >
                                    + Audio
                                  </button>
                                  <button 
                                    className="edit-button" 
                                    onClick={() => handleEditCategory(category)}
                                    style={{ fontSize: '13px', padding: '6px 12px' }}
                                  >
                                    Edit
                                  </button>
                                  {category.DEFAULT !== 1 && (
									<button 
										className="delete-button" 
										onClick={() => handleDeleteCategory(category)}
										style={{ fontSize: '13px', padding: '6px 12px' }}
									>
										Delete
									</button>
								  )}
                                </div>
                              </div>
                              
                              {/* Items Table (3rd Level) */}
                              {isCategoryExpanded && (
                                <div className="category-content items-table-container">
                                  {categoryItems.length === 0 ? (
                                    <div className="no-items-message">
                                      No items in this category. Click "+ Text", "+ Image", "+ Video", or "+ Audio" to add items.
                                    </div>
                                  ) : (
                                    <>
                                      <div className="drag-instructions">
                                        <small>
                                          💡 Drag and drop rows to reorder items within this category
                                        </small>
                                      </div>
                                      
                                      <table className="items-table">
                                        <thead>
                                          <tr className="table-header">
                                            <th style={{ width: '50px' }}>Seq</th>
                                            <th style={{ width: '80px' }}>Type</th>
                                            <th>Title</th>
                                            <th>Content/Media</th>
                                            <th style={{ width: '80px' }}>Status</th>
                                            <th style={{ width: '150px' }}>Actions</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {categoryItems.map((item, index) => (
                                            <tr 
                                              key={item.PKKEY}
                                              className={`table-row draggable-row ${isDragging && draggedItem === index && draggedCategoryId === category.PKKEY ? 'dragging' : ''}`}
                                              draggable={!loading && !isSequenceUpdating}
                                              onDragStart={(e) => handleDragStart(e, category.PKKEY, index, item)}
                                              onDragEnd={handleDragEnd}
                                              onDragOver={(e) => handleDragOver(e, category.PKKEY, index)}
                                              onDrop={(e) => handleDrop(e, category.PKKEY, index)}
                                              style={{
                                                borderTop: dragOverIndex === index && draggedCategoryId === category.PKKEY ? '3px solid #007bff' : 'none'
                                              }}
                                            >
                                              <td className="table-cell drag-handle" style={{ textAlign: 'center', fontWeight: 'bold' }}>
                                                {item.SEQUENCE}
                                              </td>
                                              <td className="table-cell">
                                                <span style={{
                                                  padding: '4px 8px',
                                                  borderRadius: '4px',
                                                  fontSize: '12px',
                                                  fontWeight: 'bold',
                                                  backgroundColor: item.TYPE === 1 ? '#e3f2fd' : item.TYPE === 2 ? '#fff3e0' : item.TYPE === 3 ? '#f3e5f5' : '#fff8e1',
                                                  color: item.TYPE === 1 ? '#1976d2' : item.TYPE === 2 ? '#f57c00' : item.TYPE === 3 ? '#7b1fa2' : '#e65100'
                                                }}>
                                                  {getTypeLabel(item.TYPE)}
                                                </span>
                                              </td>
                                              <td className="table-cell" style={{ fontWeight: '500' }}>
                                                {item.TITLE}
                                              </td>
                                              <td className="table-cell">
                                                {item.TYPE === 1 && (
                                                  <div style={{ 
                                                    maxWidth: '300px', 
                                                    overflow: 'hidden', 
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                  }}>
                                                    {item.TEXT_CONTENT}
                                                  </div>
                                                )}
                                                {item.TYPE === 2 && item.MEDIANAME && (
                                                  <img 
                                                    src={getCurrentMediaUrl(item)} 
                                                    alt={item.TITLE}
                                                    onError={(e) => handleMediaError(e, item.MEDIANAME)}
                                                    style={{ 
                                                      maxWidth: '100px', 
                                                      maxHeight: '100px',
                                                      objectFit: 'cover',
                                                      borderRadius: '4px',
                                                      border: '1px solid #ddd'
                                                    }}
                                                  />
                                                )}
                                                {item.TYPE === 3 && item.MEDIANAME && (
                                                  renderVideoWithLoading(
                                                    getCurrentMediaUrl(item),
                                                    `table-${item.PKKEY}`,
                                                    { maxWidth: '150px', maxHeight: '100px' }
                                                  )
                                                )}
                                                {item.TYPE === 4 && item.MEDIANAME && (
                                                  <audio
                                                    controls
                                                    src={getCurrentMediaUrl(item)}
                                                    style={{ maxWidth: '220px', height: '40px' }}
                                                  >
                                                    Your browser does not support the audio element.
                                                  </audio>
                                                )}
                                              </td>
                                              <td className="table-cell">
                                                <span className={item.STATUS === 1 ? 'status-active' : 'status-inactive'}>
                                                  {item.STATUS === 1 ? 'Active' : 'Inactive'}
                                                </span>
                                              </td>
                                              <td className="table-cell">
                                                <div className="action-buttons-inline">
                                                  <button 
                                                    className="edit-button-sm" 
                                                    onClick={() => handleEdit(item)}
                                                    disabled={loading}
                                                  >
                                                    Edit
                                                  </button>
                                                  <button 
                                                    className="delete-button-sm" 
                                                    onClick={() => handleDelete(item)}
                                                    disabled={loading}
                                                  >
                                                    Delete
                                                  </button>
                                                </div>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
        
        {/* Add Group Modal */}
        {showGroupModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="modal-title">Add New Group</h2>
              
              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}
              
              <form onSubmit={handleGroupSubmit}>
                <div className="form-group">
                  <label className="form-label">Group Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={groupForm.title}
                    onChange={handleGroupInputChange}
                    className="form-input"
                    required
                    autoComplete="off"
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    name="description"
                    value={groupForm.description}
                    onChange={handleGroupInputChange}
                    className="form-textarea"
                    rows="3"
                    disabled={loading}
                  />
                </div>
                
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="cancel-button"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Adding...' : 'Add Group'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* Edit Group Modal */}
        {showEditGroupModal && editingGroup && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="modal-title">Edit Group</h2>
              
              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}
              
              <form onSubmit={handleEditGroupSubmit}>
                <div className="form-group">
                  <label className="form-label">Group Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={editGroupForm.title}
                    onChange={handleEditGroupInputChange}
                    className="form-input"
                    required
                    autoComplete="off"
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    name="description"
                    value={editGroupForm.description}
                    onChange={handleEditGroupInputChange}
                    className="form-textarea"
                    rows="3"
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Status *</label>
                  <select
                    name="status"
                    value={editGroupForm.status}
                    onChange={handleEditGroupInputChange}
                    className="form-input"
                    required
                    disabled={loading}
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
                
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="cancel-button"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update Group'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* Delete Group Modal */}
        {showDeleteGroupModal && deletingGroup && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '400px' }}>
              <h2 className="modal-title">Confirm Delete</h2>
              
              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}
              
              <div style={{ marginBottom: '20px' }}>
                <p>Are you sure you want to delete the group 
                  <span style={{ fontWeight: 'bold', color: '#333' }}> "{deletingGroup.TITLE}"</span>?
                </p>
                <p style={{ color: '#dc3545', fontSize: '14px', marginTop: '10px' }}>
                  This group contains {deletingGroup.CATEGORY_COUNT} categor{deletingGroup.CATEGORY_COUNT === 1 ? 'y' : 'ies'}. 
                  You must remove or reassign all categories before deleting this group.
                </p>
              </div>
              
              <div className="form-actions">
                <button
                  type="button"
                  onClick={closeModals}
                  className="cancel-button"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteGroup}
                  className="submit-button"
                  disabled={loading}
                  style={{
                    backgroundColor: '#dc3545',
                    borderColor: '#dc3545'
                  }}
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Add Category Modal */}
        {showCategoryModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="modal-title">Add New Category</h2>
              
              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}
              
              <form onSubmit={handleCategorySubmit}>
                <div className="form-group">
                  <label className="form-label">Group *</label>
                  <select
                    name="groupId"
                    value={categoryForm.groupId || ''}
                    onChange={handleCategoryInputChange}
                    className="form-input"
                    required
                    disabled={loading}
                  >
                    <option value="">Select a group</option>
                    {groups.map(group => (
                      <option key={group.PKKEY} value={group.PKKEY}>
                        {group.TITLE}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Category Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={categoryForm.title}
                    onChange={handleCategoryInputChange}
                    className="form-input"
                    required
                    autoComplete="off"
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Keywords *</label>
                  <input
                    type="text"
                    name="keywords"
                    value={categoryForm.keywords}
                    onChange={handleCategoryInputChange}
                    className="form-input"
                    required
                    autoComplete="off"
                    disabled={loading}
                    placeholder="e.g., greeting, welcome, introduction"
                  />
                  <small className="form-hint">
                    Enter keywords separated by commas
                  </small>
                </div>
                
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="cancel-button"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Adding...' : 'Add Category'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* Edit Category Modal */}
        {showEditCategoryModal && editingCategory && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="modal-title">Edit Category</h2>
              
              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}
              
              <form onSubmit={handleEditCategorySubmit}>
                <div className="form-group">
                  <label className="form-label">Group *</label>
                  <select
                    name="groupId"
                    value={editCategoryForm.groupId || ''}
                    onChange={handleEditCategoryInputChange}
                    className="form-input"
                    required
                    disabled={loading}
                  >
                    <option value="">Select a group</option>
                    {groups.map(group => (
                      <option key={group.PKKEY} value={group.PKKEY}>
                        {group.TITLE}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Category Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={editCategoryForm.title}
                    onChange={handleEditCategoryInputChange}
                    className="form-input"
                    required
                    autoComplete="off"
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Keywords *</label>
                  <input
                    type="text"
                    name="keywords"
                    value={editCategoryForm.keywords}
                    onChange={handleEditCategoryInputChange}
                    className="form-input"
                    required
                    autoComplete="off"
                    disabled={loading}
                  />
                </div>
                
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="cancel-button"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update Category'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* Delete Category Modal */}
        {showDeleteCategoryModal && deletingCategory && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '400px' }}>
              <h2 className="modal-title">Confirm Delete</h2>
              
              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}
              
              <div style={{ marginBottom: '20px' }}>
                <p>Are you sure you want to delete the category 
                  <span style={{ fontWeight: 'bold', color: '#333' }}> "{deletingCategory.TITLE}"</span>?
                </p>
              </div>
              
              <div className="form-actions">
                <button
                  type="button"
                  onClick={closeModals}
                  className="cancel-button"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteCategory}
                  className="submit-button"
                  disabled={loading}
                  style={{
                    backgroundColor: '#dc3545',
                    borderColor: '#dc3545'
                  }}
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Text Modal */}
        {showTextModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="modal-title">Add Text Entry</h2>
              
              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}
              
              <form onSubmit={handleTextSubmit}>
                <div className="form-group">
                  <label className="form-label">Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={textForm.title}
                    onChange={handleTextInputChange}
                    className="form-input"
                    required
                    autoComplete="off"
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Text Content *</label>
                  <textarea
                    name="textContent"
                    value={textForm.textContent}
                    onChange={handleTextInputChange}
                    className="form-textarea"
                    rows="5"
                    required
                    disabled={loading}
                  />
                </div>
                
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="cancel-button"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Adding...' : 'Add Text'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* Image Modal */}
        {showImageModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="modal-title">Add Image Entry</h2>
              
              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}
              
              <form onSubmit={handleImageSubmit}>
                <div className="form-group">
                  <label className="form-label">Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={imageForm.title}
                    onChange={handleImageInputChange}
                    className="form-input"
                    required
                    autoComplete="off"
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Image File *</label>
                  <input
                    type="file"
                    name="image"
                    accept="image/jpeg,image/jpg,image/png,image/gif"
                    onChange={handleImageInputChange}
                    className="form-input"
                    required
                    disabled={loading}
                  />
                  <small className="form-hint">
                    Accepted formats: JPG, JPEG, PNG, GIF (Max: 50MB)
                  </small>
                  
                  {imagePreview && (
                    <div className="preview-container" style={{ marginTop: '10px' }}>
                      <label className="form-label">Preview:</label>
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '300px',
                          objectFit: 'contain',
                          borderRadius: '4px'
                        }} 
                      />
                    </div>
                  )}
                </div>
                
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="cancel-button"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Uploading...' : 'Add Image'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* Video Modal */}
        {showVideoModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="modal-title">Add Video Entry</h2>
              
              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}
              
              <form onSubmit={handleVideoSubmit}>
                <div className="form-group">
                  <label className="form-label">Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={videoForm.title}
                    onChange={handleVideoInputChange}
                    className="form-input"
                    required
                    autoComplete="off"
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Video File *</label>
                  <input
                    type="file"
                    name="video"
                    accept="video/mp4"
                    onChange={handleVideoInputChange}
                    className="form-input"
                    required
                    disabled={loading}
                  />
                  <small className="form-hint">
                    Accepted formats: MP4 only (Max: 50MB)
                  </small>
                  
                  {videoPreview && (
                    <div className="preview-container" style={{ marginTop: '10px' }}>
                      <label className="form-label">Preview:</label>
                      {renderVideoWithLoading(videoPreview, 'new-video-preview', { maxWidth: '300px', maxHeight: '200px' })}
                    </div>
                  )}
                </div>
                
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="cancel-button"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Uploading...' : 'Add Video'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* Audio Modal */}
        {showAudioModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="modal-title">Add Audio Entry</h2>
              
              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}
              
              <form onSubmit={handleAudioSubmit}>
                <div className="form-group">
                  <label className="form-label">Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={audioForm.title}
                    onChange={handleAudioInputChange}
                    className="form-input"
                    required
                    autoComplete="off"
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Audio File *</label>
                  <input
                    type="file"
                    name="audio"
                    accept="audio/mpeg,audio/mp3,audio/ogg,audio/wav,audio/aac,audio/x-m4a"
                    onChange={handleAudioInputChange}
                    className="form-input"
                    required
                    disabled={loading}
                  />
                  <small className="form-hint">
                    Accepted formats: MP3, OGG, WAV, AAC, M4A (Max: 50MB)
                  </small>
                  
                  {audioPreview && (
                    <div className="preview-container" style={{ marginTop: '10px' }}>
                      <label className="form-label">Preview:</label>
                      <audio
                        controls
                        src={audioPreview}
                        style={{ width: '100%', marginTop: '6px' }}
                      >
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                  )}
                </div>
                
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="cancel-button"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Uploading...' : 'Add Audio'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* Edit Item Modal */}
        {showEditModal && editingItem && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="modal-title">Edit {getTypeLabel(editingItem.TYPE)} Entry</h2>
              
              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}
              
              <form onSubmit={handleEditSubmit}>
                <div className="form-group">
                  <label className="form-label">Category *</label>
                  <select
                    name="categoryId"
                    value={editForm.categoryId || ''}
                    onChange={handleEditInputChange}
                    className="form-input"
                    required
                    disabled={loading}
                  >
                    <option value="">Select a category</option>
                    {categories.map(category => (
                      <option key={category.PKKEY} value={category.PKKEY}>
                        {category.TITLE}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={editForm.title}
                    onChange={handleEditInputChange}
                    className="form-input"
                    required
                    autoComplete="off"
                    disabled={loading}
                  />
                </div>

                {editingItem.TYPE === 1 && (
                  <div className="form-group">
                    <label className="form-label">Text Content *</label>
                    <textarea
                      name="textContent"
                      value={editForm.textContent}
                      onChange={handleEditInputChange}
                      className="form-textarea"
                      rows="5"
                      required
                      disabled={loading}
                    />
                  </div>
                )}
                
                {editingItem.TYPE === 2 && (
                  <div className="form-group">
                    <label className="form-label">Replace Image (Optional)</label>
                    <input
                      type="file"
                      name="image"
                      accept="image/jpeg,image/jpg,image/png,image/gif"
                      onChange={handleEditInputChange}
                      className="form-input"
                      disabled={loading}
                    />
                    <small className="form-hint">
                      Leave empty to keep current image
                    </small>
                    
                    {editImagePreview && (
                      <div className="preview-container" style={{ marginTop: '10px' }}>
                        <label className="form-label">Current/Preview Image:</label>
                        <img 
                          src={editImagePreview} 
                          alt="Preview" 
                          onError={(e) => handleMediaError(e, editingItem.MEDIANAME)}
                          style={{ 
                            maxWidth: '200px', 
                            maxHeight: '200px', 
                            objectFit: 'contain',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            display: 'block'
                          }} 
                        />
                      </div>
                    )}
                  </div>
                )}
                
                {editingItem.TYPE === 3 && (
                  <div className="form-group">
                    <label className="form-label">Replace Video (Optional)</label>
                    <input
                      type="file"
                      name="video"
                      accept="video/mp4"
                      onChange={handleEditInputChange}
                      className="form-input"
                      disabled={loading}
                    />
                    <small className="form-hint">
                      Leave empty to keep current video
                    </small>
                    
                    {editVideoPreview && (
                      <div className="preview-container" style={{ marginTop: '10px' }}>
                        <label className="form-label">Current/Preview Video:</label>
                        {renderVideoWithLoading(editVideoPreview, editingItem.MEDIANAME || 'edit-preview', { maxWidth: '300px', maxHeight: '200px' })}
                      </div>
                    )}
                  </div>
                )}

                {editingItem.TYPE === 4 && (
                  <div className="form-group">
                    <label className="form-label">Replace Audio (Optional)</label>
                    <input
                      type="file"
                      name="audio"
                      accept="audio/mpeg,audio/mp3,audio/ogg,audio/wav,audio/aac,audio/x-m4a"
                      onChange={handleEditInputChange}
                      className="form-input"
                      disabled={loading}
                    />
                    <small className="form-hint">
                      Leave empty to keep current audio. Accepted: MP3, OGG, WAV, AAC, M4A
                    </small>

                    {editAudioPreview && (
                      <div className="preview-container" style={{ marginTop: '10px' }}>
                        <label className="form-label">Current/Preview Audio:</label>
                        <audio
                          controls
                          src={editAudioPreview}
                          style={{ width: '100%', marginTop: '6px' }}
                        >
                          Your browser does not support the audio element.
                        </audio>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="form-group">
                  <label className="form-label">Status *</label>
                  <select
                    name="status"
                    value={editForm.status}
                    onChange={handleEditInputChange}
                    className="form-input"
                    required
                    disabled={loading}
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
                
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="cancel-button"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* Delete Item Modal */}
        {showDeleteModal && deletingItem && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '400px' }}>
              <h2 className="modal-title">Confirm Delete</h2>
              
              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}
              
              <div style={{ marginBottom: '20px' }}>
                <p>Are you sure you want to delete this 
                  <span style={{ fontWeight: 'bold', color: '#333' }}> "{deletingItem.TITLE}" </span> 
                  {getTypeLabel(deletingItem.TYPE).toLowerCase()} entry?</p>
              </div>
              
              <div className="form-actions">
                <button
                  type="button"
                  onClick={closeModals}
                  className="cancel-button"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="submit-button"
                  disabled={loading}
                  style={{
                    backgroundColor: '#dc3545',
                    borderColor: '#dc3545'
                  }}
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MasterOpening;